<template>
  <div class="profit-loss">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3; grid-template-columns: repeat(3, minmax(130px, 1fr))">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Levels</label>
          <select v-model="filters.depth" class="form-select">
            <option v-for="choice in DEPTH_CHOICES" :key="String(choice.id)" :value="choice.id">{{ choice.text }}</option>
          </select>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-trending-up"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Income</div>
          <div class="rp-card-value">{{ formatMoney(summary.income) }}</div>
          <div class="rp-card-sub">
            <span :class="netClass(change(summary.income, summary.previous.income))">{{ changeText(summary.income, summary.previous.income) }}</span>
            vs previous period
          </div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-chart-pie"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Gross Profit</div>
          <div class="rp-card-value" :class="{ 'text-danger': Number(summary.gross_profit) < 0 }">{{ formatMoney(summary.gross_profit) }}</div>
          <div class="rp-card-sub">{{ summary.gross_margin }}% margin · cost of sales {{ formatMoney(summary.cogs) }}</div>
        </div>
      </div>
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-receipt"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Operating Expenses</div>
          <div class="rp-card-value">{{ formatMoney(summary.expenses) }}</div>
          <div class="rp-card-sub">
            <span :class="netClass(-change(summary.expenses, summary.previous.expenses))">{{ changeText(summary.expenses, summary.previous.expenses) }}</span>
            vs previous period
          </div>
        </div>
      </div>
      <div class="rp-card" :class="Number(summary.net_profit) < 0 ? 'tone-red' : 'tone-teal'">
        <span class="rp-card-icon"><i class="ti" :class="Number(summary.net_profit) < 0 ? 'ti-trending-down' : 'ti-coin'"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">{{ Number(summary.net_profit) < 0 ? 'Net Loss' : 'Net Profit' }}</div>
          <div class="rp-card-value" :class="Number(summary.net_profit) < 0 ? 'text-danger' : 'text-success'">
            {{ formatMoney(summary.net_profit) }}
          </div>
          <div class="rp-card-sub">{{ summary.net_margin }}% of income · before: {{ formatMoney(summary.previous.net_profit) }}</div>
        </div>
      </div>
    </div>

    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Where the income went</div>
        <div class="rp-bars">
          <div v-for="step in flowBars" :key="step.label">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ step.label }}</span>
              <span class="fw-semibold text-nowrap" :class="step.text">{{ formatMoney(step.value) }}</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="step.tone" :style="{ width: `${step.percent}%` }"></div></div>
          </div>
        </div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Largest costs</div>
        <ul v-if="summary.top_expenses.length" class="rp-ranked">
          <li v-for="(entry, index) in summary.top_expenses" :key="entry.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="entry.name">{{ entry.name }}</div>
              <small class="text-muted">{{ entry.percent }}% of all costs</small>
            </div>
            <span class="fw-semibold text-nowrap">{{ formatMoney(entry.amount) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No expenses in the period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Against the previous period</div>
        <small class="text-muted d-block mb-2">
          {{ formatDate(summary.previous.date_from) }} – {{ formatDate(summary.previous.date_to) }}
        </small>
        <ul class="rp-highlights">
          <li v-for="item in comparison" :key="item.label" :class="item.tone">
            <span class="hl-icon"><i class="ti" :class="item.icon"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">{{ item.label }}</small>
              <div class="fw-semibold">{{ formatMoney(item.now) }}</div>
              <small :class="item.goodUp ? netClass(change(item.now, item.before)) : netClass(-change(item.now, item.before))">
                {{ changeText(item.now, item.before) }} · was {{ formatMoney(item.before) }}
              </small>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold">Profit &amp; Loss <span class="text-muted fw-normal small">· {{ periodText() }}</span></div>
      </div>
      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th>Particulars</th>
              <th class="text-end">Amount</th>
              <th class="text-end">% of Income</th>
              <th class="text-end">Previous Period</th>
              <th class="text-end">Change</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key" :class="rowClass(row)">
              <td>
                <span class="acc-indent" :style="{ width: `${(row.level || 0) * 1.1}rem` }"></span>
                <button v-if="row.code" type="button" class="acc-link" title="Open this account's ledger" @click="openLedger(row.code)">
                  {{ row.name }}
                </button>
                <template v-else>{{ row.name }}</template>
                <span v-if="row.code" class="acc-code ms-1">{{ row.code }}</span>
              </td>
              <template v-if="row.kind !== 'section'">
                <td class="text-end" :class="{ 'text-danger': Number(row.amount) < 0 }">{{ formatMoney(row.amount) }}</td>
                <td class="text-end text-muted">{{ row.percent }}%</td>
                <td class="text-end text-muted">{{ formatMoney(row.previous) }}</td>
                <td class="text-end small" :class="changeClass(row)">{{ changeText(row.amount, row.previous) }}</td>
              </template>
              <td v-else colspan="4"></td>
            </tr>
            <tr v-if="!loading && !rows.length">
              <td colspan="5">
                <div class="rp-empty">
                  <i class="ti ti-report-money"></i>
                  <div class="fw-semibold">Nothing to show</div>
                  <small class="text-muted">Try another period.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th>{{ Number(summary.net_profit) < 0 ? 'Net Loss' : 'Net Profit' }}</th>
              <th class="text-end" :class="Number(summary.net_profit) < 0 ? 'text-danger' : 'text-success'">{{ formatMoney(summary.net_profit) }}</th>
              <th class="text-end">{{ summary.net_margin }}%</th>
              <th class="text-end">{{ formatMoney(summary.previous.net_profit) }}</th>
              <th class="text-end small">{{ changeText(summary.net_profit, summary.previous.net_profit) }}</th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
    <p class="rp-hint mt-2 mb-0">
      Income = credits − debits of the income heads (discounts allowed reduce it); cost of sales is the
      {{ summary.cogs_head || 'cost of sales' }} group from the voucher setup; operating expenses are the other expense
      heads. The previous period has the same number of days, right before. Approved postings of {{ options.store || 'the store' }}.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import accountingReport, { DEPTH_CHOICES } from './accountingReportMixin';

const EMPTY_SUMMARY = {
  income: 0, cogs: 0, gross_profit: 0, expenses: 0, net_profit: 0, gross_margin: 0, net_margin: 0,
  previous: { date_from: '', date_to: '', income: 0, cogs: 0, expenses: 0, net_profit: 0 },
  cogs_head: null, top_expenses: [], income_lines: [],
};
const EXPENSE_SECTIONS = ['cogs', 'expense'];

export default {
  name: 'ProfitLossReport',
  mixins: [inventoryReport, periodReport, accountingReport],

  reportUrl: apiUrls.profit_loss_report.get,
  fileName: 'profit_and_loss',
  columns: [],

  data() {
    return {
      DEPTH_CHOICES,
      filters: { ...defaultPeriod(), depth: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
      section: {},
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged || this.filters.depth !== null;
    },

    visibleRows() {
      return this.rows;
    },

    flowBars() {
      const s = this.summary;
      const net = Number(s.net_profit) || 0;
      const steps = [
        { label: 'Income', value: s.income, tone: 'success', text: '' },
        { label: 'Cost of sales', value: s.cogs, tone: 'warning', text: '' },
        { label: 'Gross profit', value: s.gross_profit, tone: 'info', text: Number(s.gross_profit) < 0 ? 'text-danger' : '' },
        { label: 'Operating expenses', value: s.expenses, tone: 'danger', text: '' },
        { label: net < 0 ? 'Net loss' : 'Net profit', value: net, tone: net < 0 ? 'danger' : 'success', text: net < 0 ? 'text-danger' : 'text-success' },
      ];
      const max = Math.max(0, ...steps.map((st) => Math.abs(Number(st.value) || 0)));
      return steps.map((st) => {
        const v = Math.abs(Number(st.value) || 0);
        return { ...st, percent: max && v ? Math.max(4, Math.round((v / max) * 100)) : 0 };
      });
    },

    comparison() {
      const s = this.summary;
      const p = s.previous;
      return [
        { label: 'Income', now: s.income, before: p.income, goodUp: true, icon: 'ti-trending-up', tone: 'tone-teal' },
        { label: 'Cost of sales + expenses', now: Number(s.cogs) + Number(s.expenses), before: Number(p.cogs) + Number(p.expenses), goodUp: false, icon: 'ti-receipt', tone: 'tone-orange' },
        { label: 'Net profit', now: s.net_profit, before: p.net_profit, goodUp: true, icon: 'ti-coin', tone: 'tone-navy' },
      ];
    },
  },

  watch: {
    'filters.depth'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      return { ...this.periodParams(), ...(this.filters.depth ? { depth: this.filters.depth } : {}) };
    },

    resetExtraFilters() {
      return { ...defaultPeriod(this.today), depth: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      // Which section each row belongs to, so a rise in costs reads as bad and a rise in income as good.
      let current = null;
      this.section = {};
      this.rows.forEach((row) => {
        if (row.kind === 'section') current = row.key.replace('-head', '');
        this.section[row.key] = row.kind === 'result' ? null : current;
      });
    },

    rowClass(row) {
      return {
        section: 'acc-section', group: 'acc-group', subtotal: 'acc-subtotal', result: 'acc-result',
      }[row.kind] || '';
    },

    change(now, before) {
      return (Number(now) || 0) - (Number(before) || 0);
    },

    changeText(now, before) {
      const diff = this.change(now, before);
      const base = Math.abs(Number(before) || 0);
      if (Math.abs(diff) < 0.005) return 'no change';
      if (!base) return `${diff > 0 ? '+' : '−'}${this.formatMoney(Math.abs(diff))}`;
      return `${diff > 0 ? '+' : '−'}${Math.abs((diff / base) * 100).toFixed(1)}%`;
    },

    changeClass(row) {
      const diff = this.change(row.amount, row.previous);
      if (Math.abs(diff) < 0.005) return 'text-muted';
      const cost = EXPENSE_SECTIONS.includes(this.section[row.key]);
      return (diff > 0) !== cost ? 'text-success' : 'text-danger';
    },

    printReport() {
      const s = this.summary;
      const levels = DEPTH_CHOICES.find((c) => c.id === this.filters.depth)?.text;
      this.printStatement(
        'Profit & Loss',
        [this.periodText(), `Levels: ${levels}`, this.storeLabel(),
          `Previous period: ${this.formatDate(s.previous.date_from)} – ${this.formatDate(s.previous.date_to)}`],
        [['Income', this.formatMoney(s.income)], ['Gross profit', this.formatMoney(s.gross_profit)],
          ['Expenses', this.formatMoney(s.expenses)], [Number(s.net_profit) < 0 ? 'Net loss' : 'Net profit', this.formatMoney(s.net_profit)]],
        [
          { label: 'Particulars', value: (r) => `${'\u00a0\u00a0\u00a0'.repeat(r.level || 0)}${r.name}` },
          { label: 'Code', value: (r) => r.code || '' },
          { label: 'Amount', value: (r) => (r.kind === 'section' ? '' : this.formatMoney(r.amount)), num: true },
          { label: '% of Income', value: (r) => (r.kind === 'section' ? '' : `${r.percent}%`), num: true },
          { label: 'Previous Period', value: (r) => (r.kind === 'section' ? '' : this.formatMoney(r.previous)), num: true },
        ],
        this.rows,
        {
          Particulars: Number(s.net_profit) < 0 ? 'Net Loss' : 'Net Profit', Amount: this.formatMoney(s.net_profit),
          '% of Income': `${s.net_margin}%`, 'Previous Period': this.formatMoney(s.previous.net_profit),
        },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
<style scoped src="./accountingReport.css"></style>
